import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { cloneDeep, isEqual } from "lodash";
import { MdFullscreen, MdFullscreenExit } from "react-icons/md";
import { FaCheckCircle } from "react-icons/fa";
import LoadingCircle from "../../utility/LoadingCircle";
import PermissionGuard from "../../User/PermissionGuard";
import PermissionsMap from "../../User/PermissionsMap";
import ReportStructureTree from "./ReportStructureTree";
import ReportPdfPreview from "./ReportPdfPreview";
import CoverEditModal from "./CoverEditModal";
import { ensureCoverInstructions, reportFieldsFromInstructions } from "./coverInstructions";
import { ensurePdfSettings } from "./pdfSettings";

const ReportV2 = ({ api, token, hasUnsavedFormData, setHasUnsavedFormData }) => {
	const { reportId } = useParams();
	const [catalog, setCatalog] = useState({});
	const [dataToSave, setDataToSave] = useState(null);
	const [initialData, setInitialData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [saveOverlay, setSaveOverlay] = useState(null);
	const [focusKey, setFocusKey] = useState(null);
	const [focusNonce, setFocusNonce] = useState(0);

	const focusOn = (key) => {
		if (!key) {
			setFocusKey(null);
			return;
		}
		setFocusKey((prev) => (prev === key ? null : key));
		setFocusNonce((n) => n + 1);
	};
	const [loadError, setLoadError] = useState("");
	const [isFullscreen, setIsFullscreen] = useState(true);
	const [editingCover, setEditingCover] = useState(false);
	const [previewReady, setPreviewReady] = useState(false);
	const markPreviewReady = useCallback(() => setPreviewReady(true), []);

	useEffect(() => {
		document.title = "Report Builder v2";
	}, []);

	useEffect(() => {
		if (!setHasUnsavedFormData || !dataToSave || !initialData) return;
		setHasUnsavedFormData(!isEqual(dataToSave, initialData));
	}, [dataToSave, initialData, setHasUnsavedFormData]);

	useEffect(() => {
		const handleBeforeUnload = (event) => {
			if (!hasUnsavedFormData) return;
			event.preventDefault();
			event.returnValue = "";
		};
		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [hasUnsavedFormData]);

	useEffect(() => {
		if (!isFullscreen) return;
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		const onKey = (e) => {
			if (e.key === "Escape") setIsFullscreen(false);
		};
		window.addEventListener("keydown", onKey);
		return () => {
			document.body.style.overflow = prev;
			window.removeEventListener("keydown", onKey);
		};
	}, [isFullscreen]);

	useEffect(() => {
		if (!api || !token || isNaN(Number(reportId))) return;

		let cancelled = false;
		setLoading(true);
		setLoadError("");

		Promise.all([
			fetch(`${api}/report/${reportId}`, {
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					Authorization: "Bearer " + token,
				},
				mode: "cors",
			}).then((res) => {
				if (!res.ok) throw new Error("Failed to load report");
				return res.json();
			}),
			fetch(`${api}/report/project-templates`, {
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					Authorization: "Bearer " + token,
				},
				mode: "cors",
			}).then((res) => {
				if (!res.ok) throw new Error("Failed to load templates");
				return res.json();
			}),
		])
			.then(([report, templatesRes]) => {
				if (cancelled) return;
				const payload = ensurePdfSettings(
					ensureCoverInstructions({
						reportName: report.reportName,
						saveName: report.saveName,
						coverImgName: report.selectedImgName,
						templateData: report.templateData || {},
						coverInstructions: report.coverInstructions,
						pdfSettings: report.pdfSettings,
					})
				);
				setDataToSave(cloneDeep(payload));
				setInitialData(cloneDeep(payload));
				setCatalog(templatesRes.formattedTemplateData || {});
				document.title = `Report Builder v2: ${report.reportName}`;
			})
			.catch((err) => {
				if (cancelled) return;
				setLoadError(err.message || "Unable to load report");
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [api, token, reportId]);

	const save = () => {
		if (!dataToSave || saveOverlay) return;
		setSaveOverlay("saving");
		const startedAt = Date.now();
		const coverInstructions = dataToSave.coverInstructions;
		const authHeaders = {
			"Content-Type": "application/json",
			Accept: "application/json",
			Authorization: "Bearer " + token,
		};
		const reportBody = { ...dataToSave };
		delete reportBody.coverInstructions;
		fetch(`${api}/report/${reportId}`, {
			method: "PUT",
			headers: authHeaders,
			mode: "cors",
			body: JSON.stringify(reportBody),
		})
			.then((res) => {
				if (!res.ok) throw new Error("Save failed");
				return fetch(`${api}/report/${reportId}`, {
					method: "GET",
					headers: authHeaders,
					mode: "cors",
				});
			})
			.then((res) => {
				if (!res.ok) throw new Error("Saved, but failed to refresh");
				return res.json();
			})
			.then((report) => {
				const payload = ensurePdfSettings(
					ensureCoverInstructions({
						reportName: report.reportName,
						saveName: report.saveName,
						coverImgName: report.selectedImgName,
						templateData: report.templateData || {},
						coverInstructions: report.coverInstructions || coverInstructions,
						pdfSettings: report.pdfSettings,
					})
				);
				setDataToSave(cloneDeep(payload));
				setInitialData(cloneDeep(payload));
				if (setHasUnsavedFormData) setHasUnsavedFormData(false);
				const remaining = Math.max(0, 1000 - (Date.now() - startedAt));
				return new Promise((resolve) => setTimeout(resolve, remaining));
			})
			.then(() => {
				setSaveOverlay("saved");
			})
			.catch((err) => {
				setSaveOverlay(null);
				alert(err.message === "Saved, but failed to refresh" ? err.message : "Could not save report.");
			});
	};

	useEffect(() => {
		if (saveOverlay !== "saved") return;
		const timer = setTimeout(() => setSaveOverlay(null), 1200);
		return () => clearTimeout(timer);
	}, [saveOverlay]);

	if (loading) {
		return (
			<div className="flex justify-center items-center bg-stone-100" style={{ height: "100dvh" }}>
				<LoadingCircle />
			</div>
		);
	}

	if (loadError || !dataToSave) {
		return (
			<div className="p-6">
				<p className="text-red-700 mb-3">{loadError || "Report not found"}</p>
				<Link to="/reports" className="text-blue-600 underline">
					Back to reports
				</Link>
			</div>
		);
	}

	return (
		<div
			className={`${isFullscreen ? "fixed inset-0 z-[300]" : "relative"} flex flex-col overflow-hidden bg-stone-100 text-stone-900`}
			style={{ height: "100dvh" }}>
			{!previewReady ? (
				<div className="absolute inset-0 z-[80] flex items-center justify-center bg-stone-100">
					<LoadingCircle />
				</div>
			) : null}
			<div className="shrink-0 bg-stone-800 text-stone-50 px-4 py-2.5 flex flex-wrap items-center justify-between gap-3">
				<div className="min-w-0">
					<p className="text-lg font-semibold truncate">{dataToSave.reportName}</p>
					<p className="text-xs text-stone-300">
						Report Builder v2 · tree + PDF preview
						{dataToSave.saveName ? ` · ${dataToSave.saveName}` : ""}
					</p>
				</div>
				<div className="flex flex-wrap gap-2 items-center">
					<Link to={`/report/${reportId}`} className="text-sm px-3 py-1.5 rounded bg-stone-700 hover:bg-stone-600">
						Open classic builder
					</Link>
					<PermissionGuard permission={PermissionsMap.REPORTS_UPDATE}>
						<button
							type="button"
							onClick={() => setEditingCover(true)}
							className="text-sm px-3 py-1.5 rounded bg-stone-700 hover:bg-stone-600">
							Edit cover
						</button>
					</PermissionGuard>
					<button
						type="button"
						onClick={() => setIsFullscreen((v) => !v)}
						className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded bg-stone-700 hover:bg-stone-600"
						title={isFullscreen ? "Exit fullscreen (Esc)" : "Fullscreen — hide app chrome"}>
						{isFullscreen ? <MdFullscreenExit className="text-lg" /> : <MdFullscreen className="text-lg" />}
						{isFullscreen ? "Exit full screen" : "Fullscreen"}
					</button>
					<PermissionGuard permission={PermissionsMap.REPORTS_UPDATE}>
						<button
							type="button"
							onClick={save}
							disabled={!!saveOverlay || isEqual(dataToSave, initialData)}
							className="text-sm px-3 py-1.5 rounded bg-amber-500 text-stone-900 font-medium hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed">
							Save
						</button>
					</PermissionGuard>
				</div>
			</div>

			<div className="grid lg:grid-cols-[minmax(280px,380px)_minmax(0,1fr)] flex-1 min-h-0 gap-0 overflow-hidden">
				<aside className="bg-white border-r border-stone-200 flex flex-col min-h-0 overflow-hidden h-full max-h-full">
					<div className="shrink-0 px-3 py-2 border-b border-stone-100">
						<p className="text-xs uppercase tracking-wide text-stone-500 font-semibold">Structure</p>
						<p className="text-[11px] text-stone-400">Add templates, then include sections &amp; fields</p>
						<p className="text-[11px] text-amber-800/80 mt-1.5 leading-snug">
							Sections and fields come from all versions of each template. The <span className="font-medium">vN</span>{" "}
							badge is the latest version that still includes that name — older project versions may omit it from the
							PDF.
						</p>
					</div>
					<div className="flex-1 min-h-0 overflow-hidden p-2 flex flex-col">
						<ReportStructureTree
							catalog={catalog}
							dataToSave={dataToSave}
							onChange={setDataToSave}
							focusKey={focusKey}
							focusNonce={focusNonce}
							onFocus={focusOn}
						/>
					</div>
				</aside>

				<section className="min-h-0 overflow-hidden p-3">
					<ReportPdfPreview
						api={api}
						token={token}
						reportName={dataToSave.reportName}
						saveName={dataToSave.saveName}
						coverImgName={dataToSave.coverImgName}
						coverInstructions={dataToSave.coverInstructions}
						templateData={dataToSave.templateData}
						pdfSettings={dataToSave.pdfSettings}
						onPdfSettingsChange={(pdfSettings) => setDataToSave((prev) => ({ ...prev, pdfSettings }))}
						catalog={catalog}
						focusKey={focusKey}
						focusNonce={focusNonce}
						onFocus={focusOn}
						onReady={markPreviewReady}
					/>
				</section>
			</div>

			{editingCover ? (
				<CoverEditModal
					api={api}
					token={token}
					reportId={reportId}
					reportName={dataToSave.reportName}
					coverImgName={dataToSave.coverImgName}
					coverInstructions={dataToSave.coverInstructions}
					onClose={() => setEditingCover(false)}
					onApply={async (coverInstructions) => {
						const res = await fetch(`${api}/report/${reportId}/cover`, {
							method: "PUT",
							headers: {
								"Content-Type": "application/json",
								Accept: "application/json",
								Authorization: "Bearer " + token,
							},
							mode: "cors",
							body: JSON.stringify({ instructions: coverInstructions }),
						});
						if (!res.ok) throw new Error("Could not save cover.");
						const fields = reportFieldsFromInstructions(coverInstructions, dataToSave);
						setDataToSave((prev) => ({ ...prev, ...fields, coverInstructions }));
						setInitialData((prev) => (prev ? { ...prev, ...fields, coverInstructions } : prev));
						if (fields.reportName) document.title = `Report Builder v2: ${fields.reportName}`;
					}}
				/>
			) : null}

			{saveOverlay ? (
				<div className="absolute inset-0 z-[400] bg-black/60 flex items-center justify-center p-6">
					<div className="w-[280px] bg-white text-stone-900 rounded-xl shadow-2xl border border-stone-200 px-8 py-7 flex flex-col items-center text-center">
						{saveOverlay === "saved" ? (
							<>
								<FaCheckCircle className="text-[42px] text-emerald-600" aria-hidden="true" />
								<p className="mt-3 text-lg font-semibold text-stone-900">Saved</p>
								<p className="mt-1 text-sm text-stone-500">Your report has been updated.</p>
							</>
						) : (
							<>
								<LoadingCircle />
								<p className="mt-4 text-base font-medium text-stone-800">Saving report</p>
								<p className="mt-1 text-sm text-stone-500">Please wait a moment.</p>
							</>
						)}
					</div>
				</div>
			) : null}
		</div>
	);
};

export default ReportV2;
